/**
 * The viewer's theme preference, read defensively.
 *
 * WHY THIS EXISTS. The preference arrives from places that cannot promise a
 * valid value: a profile document whose `settings` map has no `theme` (older
 * or hand-edited documents; the auth context always builds a `settings`
 * object, so a missing key is not caught by a "no settings at all" fallback),
 * and localStorage, which the page itself writes and anything can corrupt.
 * An unchecked `undefined` became the class on <html> (so the page painted as
 * neither light nor dark), was written back to localStorage as the string
 * "undefined", and crashed checkout outright, because the Stripe appearance
 * builder indexes its fallback colours by the resolved theme.
 *
 * Anything that is not one of the three known values reads as 'system', the
 * default a new account gets. The pre-hydration script in
 * src/lib/init-theme-script.js repeats this rule in plain ES5 (it runs before
 * any bundle) and must stay in step with it.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedThemeName = 'light' | 'dark';

export function normaliseThemePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

/** The theme actually painted: the preference, with 'system' resolved against the OS. */
export function resolveThemePreference(value: unknown, systemPrefersDark: boolean): ResolvedThemeName {
  const preference = normaliseThemePreference(value);
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}
